import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { requestSensors, SensorType } from "@/api/SensorsService"
import { WearableModalType } from "@/components/WearablesModal/types"
import { ScannerConsumerType, useScanner } from "@/hooks/useScanner"
import type { AppDispatch } from "@/store"
import { fetchInmates, selectInmates } from "@/store/inmatesSlice"
import { fetchWearableSensors } from "@/store/wearableSensorsSlice"
import { Sensor } from "@/types/sensor"
import { normalizeMacAddress } from "@/utils/index"

interface UseScannedWearableProps {
   selectedInmateId: string | null
   initialModalMode?: WearableModalType
   consumerType?: ScannerConsumerType
   scannerEnabled?: boolean
   openWearableModal: () => void
   onScannedWearableSave: (inmateId: string, sensor: Sensor) => Promise<void>
   onDisconnect: (inmateId: string, wearableId?: string) => Promise<void>
}

export const useScannedWearable = ({
   selectedInmateId,
   initialModalMode,
   consumerType = ScannerConsumerType.DEFAULT,
   scannerEnabled,
   openWearableModal,
   onScannedWearableSave,
   onDisconnect,
}: UseScannedWearableProps) => {
   const dispatch = useDispatch<AppDispatch>()
   const inmates = useSelector(selectInmates)

   const [isScanning, setIsScanning] = useState(false)
   const [scannedSensor, setScannedSensor] = useState<Sensor | null>(null)
   const [wearableModalMode, setWearableModalMode] =
      useState<WearableModalType>(WearableModalType.SCAN)
   const [wearableOriginMode, setWearableOriginMode] =
      useState<WearableModalType>(WearableModalType.SCAN)
   const [swapTargetInmateId, setSwapTargetInmateId] = useState<string | null>(
      null
   )

   const { scannedId, resetScannedId } = useScanner({
      consumerType,
      enabled: scannerEnabled,
   })

   const swapTargetRef = useRef<string | null>(null)
   const isModalOpenRef = useRef(false)
   const inmatesRef = useRef(inmates)
   const onScannedWearableSaveRef = useRef(onScannedWearableSave)

   inmatesRef.current = inmates
   onScannedWearableSaveRef.current = onScannedWearableSave

   useEffect(() => {
      if (!selectedInmateId || !initialModalMode) return
      setWearableModalMode(initialModalMode)
      setWearableOriginMode(initialModalMode)
   }, [selectedInmateId, initialModalMode])

   useEffect(() => {
      if (!scannedId) return
      if (isModalOpenRef.current && !swapTargetRef.current && !selectedInmateId)
         return

      let newSensor: Sensor | null = null

      setIsScanning(true)
      dispatch(fetchWearableSensors())
      requestSensors({ type: SensorType.WEARABLE, serialNumber: scannedId })
         .then((response) => {
            if (response.data.length === 0) {
               setWearableModalMode(WearableModalType.UNKNOWN)
               setScannedSensor({
                  id: "",
                  macAddress: "",
                  serialNumber: scannedId,
                  level: "",
                  type: SensorType.WEARABLE,
               })
               return
            }
            newSensor = response.data[0]
            return requestSensors({
               type: SensorType.WEARABLE,
               serialNumber: scannedId,
               hasInmate: true,
            })
         })
         .then(async (response) => {
            if (!response || !newSensor) return
            const wearableHasInmate = response.data.length > 0
            const latestInmates = await dispatch(fetchInmates())
               .unwrap()
               .then(({ data }) => data.results)
               .catch((error) => {
                  console.error("Error fetching inmates:", error)
                  return inmatesRef.current
               })

            if (wearableHasInmate) {
               if (!swapTargetRef.current) {
                  swapTargetRef.current =
                     latestInmates.find(
                        (inmate) =>
                           normalizeMacAddress(
                              inmate.sensor?.macAddress ?? ""
                           ) === normalizeMacAddress(newSensor!.macAddress)
                     )?.id ?? null
                  setSwapTargetInmateId(swapTargetRef.current)
                  setScannedSensor(newSensor)
                  setWearableOriginMode(WearableModalType.OPTIONS)
                  setWearableModalMode(WearableModalType.OPTIONS)
               } else {
                  setScannedSensor(newSensor)
                  setWearableModalMode(WearableModalType.ALREADY_ASSIGNED)
               }
            } else if (swapTargetRef.current || selectedInmateId) {
               const swapTargetId = swapTargetRef.current ?? selectedInmateId!
               swapTargetRef.current = null
               setSwapTargetInmateId(null)
               setScannedSensor(newSensor)
               onScannedWearableSaveRef
                  .current(swapTargetId, newSensor)
                  .catch((error) => {
                     console.error("Error swapping wearable:", error)
                  })
               setWearableModalMode((prev) =>
                  prev === WearableModalType.OPTIONS
                     ? WearableModalType.SWAPPED
                     : WearableModalType.ASSIGNED
               )
            } else {
               setScannedSensor(newSensor)
               setWearableOriginMode(WearableModalType.SCANNED)
               setWearableModalMode(WearableModalType.SCANNED)
            }
         })
         .finally(() => {
            setIsScanning(false)
            openWearableModal()
            resetScannedId()
         })
   }, [
      scannedId,
      selectedInmateId,
      openWearableModal,
      resetScannedId,
      dispatch,
   ])

   const handleDisconnect = async (inmateId: string, wearableId?: string) => {
      try {
         await onDisconnect(inmateId, wearableId)
      } catch (error) {
         console.error("Error disconnecting wearable:", error)
      }
   }

   const resetScanState = () => {
      swapTargetRef.current = null
      setSwapTargetInmateId(null)
      setScannedSensor(null)
      setWearableModalMode(WearableModalType.SCAN)
      setWearableOriginMode(WearableModalType.SCAN)
      resetScannedId()
   }

   return {
      isScanning,
      scannedSensor,
      setScannedSensor,
      wearableModalMode,
      wearableOriginMode,
      swapTargetRef,
      swapTargetInmateId,
      isModalOpenRef,
      resetScanState,
      setWearableModalMode,
      handleDisconnect,
   }
}
